import { Button, Checkbox, FormControl, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { ReactNode, useContext } from 'react';
import AddIcon from '@mui/icons-material/AddCircle';
import InfoIcon from '@mui/icons-material/InfoRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { PurchaseOrderDetailsContext, PurchaseOrderDetailsValues } from '../contexts/PurchaseOrderDetailsContext';
import { LineRecord } from '../interfaces/LineRecord';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { availablePurchaseOrderLineReasons, getReason } from '../interfaces/PurchaseOrderLineReason';
import { DynamicUomSelect } from '../../../components/shared/DymanicUomSelect';
import { applyRoundAndFormat } from '../../../utils';
import { calculateLineTotalCostPrice } from '../utils/calculateTotalCostPrice';

const TableCellSort = (props: { children: ReactNode, tableKey: keyof LineRecord }) => {

  const { order,
    orderBy,
    handleSort,
  } = useContext(PurchaseOrderDetailsContext) as PurchaseOrderDetailsValues;

  return <TableCell>
    <TableSortLabel active={orderBy === props.tableKey}
      hideSortIcon
      direction={order}
      onClick={() => handleSort(props.tableKey)}>
      {props.children}
    </TableSortLabel>
  </TableCell>;
};

const PurchaseOrdersDetailsTable = () => {
  const navigate = useNavigate();
  const { handleNoteButtonClick,
    handleQuantityChange,
    handleUomsChange,
    handleReasonChange,
    sortedPoLines,
    isShowingTrafficCode,
    isReceiving,
    purchaseOrders,
    isEdited,
    isEditable,
    isCreatingReturnToVendor,
    handlePOLineFocus,
    disableUomLac,
    canDisplayCostPrice,
    canDisplayTotalCostPrice,
    totalCostPrice,
    currencyCode,
  } = useContext(PurchaseOrderDetailsContext) as PurchaseOrderDetailsValues;
  const { t } = useTranslation('purchaseOrderDetails');

  const visibleColumnCount = [
    true, // view button column
    !isReceiving, // selected
    true, // qty
    !!purchaseOrders?.showReason, // reason
    true, // uom
    true, // item
    true, // itemDescription
    true, // expectedQty
    true, // expectedUom
    canDisplayCostPrice, // unitCost
    canDisplayCostPrice, // totalCost
    true, // poLine
    isShowingTrafficCode, // tariffCode
    true, // note
  ].filter(Boolean).length;

  return <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          {!isReceiving &&
            <TableCell>
              {t('selected')}
            </TableCell>}
          <TableCell >
          </TableCell>
          <TableCellSort tableKey='quantity'>
            {t(isCreatingReturnToVendor ? 'creditedQuantity' : 'qty')}
          </TableCellSort>
          {
            purchaseOrders?.showReason &&
            <TableCell>
              {t('reason')}
            </TableCell>
          }
          <TableCellSort tableKey='itemNumber'>
            {t('item')}
          </TableCellSort>
          <TableCellSort tableKey='itemDescription'>
            {t('itemDescription')}
          </TableCellSort>
          <TableCellSort tableKey='expectedQuantity'>
            {t(isCreatingReturnToVendor ? 'receivedQty' : 'expectedQty')}
          </TableCellSort>
          {canDisplayCostPrice && <TableCellSort tableKey='lineNumber'>
            {t('unitCost')}
          </TableCellSort>}
          {canDisplayCostPrice && <TableCellSort tableKey='lineNumber'>
            {t('totalCost')}
          </TableCellSort>}
          <TableCellSort tableKey='lineNumber'>
            {t('poLine')}
          </TableCellSort>
          {isShowingTrafficCode &&
            <TableCellSort tableKey='tariffCode'>
              {t('tariffCode')}
            </TableCellSort>}
          <TableCellSort tableKey='note'>
            {t('note')}
          </TableCellSort>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedPoLines?.map((PoLine) =>
          <TableRow
            key={PoLine.id}
            hover
            onFocus={() => handlePOLineFocus(PoLine.id ?? 0)}
          >
            {!isReceiving &&
              <TableCell>
                <Checkbox
                  disabled
                  checked={isEdited(PoLine)}
                />
              </TableCell>}
            <TableCell>
              <Button size='md'
                sx={{ pr: '5px', pl: '5px' }}
                variant='primary'
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/store/item/${PoLine.id}?sku=${PoLine.itemNumber}`);
                }}>
                {t('view')}
              </Button>
            </TableCell>
            <TableCell>
              <DynamicUomSelect
                quantity={Number(PoLine.quantity)}
                onQuantityChange={handleQuantityChange(PoLine.id ?? -1)}
                onUomChange={handleUomsChange(PoLine.id ?? -1)}
                uoms={PoLine.uomList}
                disabledUom={disableUomLac}
                disabled={!(isEditable || isCreatingReturnToVendor) || PoLine.isDepositFeeItem}
                seletedUom={PoLine.uom} />
            </TableCell>
            {
              purchaseOrders?.showReason &&
              <TableCell>
                {
                  PoLine.hasDiscrepancy && isEditable &&
                  <FormControl fullWidth>
                    <Select
                      value={PoLine.reasonForCreditRequest ?? ''}
                      onChange={($event) => handleReasonChange(PoLine.id, $event.target.value)}
                      onClick={($event) => $event.stopPropagation()}
                      displayEmpty
                      renderValue={(value) => {
                        const selectedItem = availablePurchaseOrderLineReasons.find(item => item.value === value);
                        return selectedItem ? t(getReason(selectedItem.key)) : '';
                      }}
                    >
                      {availablePurchaseOrderLineReasons.map((item) => (
                        <MenuItem key={item.key}
                          value={item.value}>
                          {t(getReason(item.key))}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                }
                {
                  !isEditable &&
                  <Typography>{PoLine.reasonForCreditRequest}</Typography>
                }
              </TableCell>
            }
            <TableCell>
              {PoLine.itemNumber}
            </TableCell>
            <TableCell>
              {PoLine.itemDescription}
            </TableCell>
            <TableCell>
              <DynamicUomSelect
                disabled
                quantity={isCreatingReturnToVendor ? Number(PoLine.originalQuantity) : PoLine.expectedQuantity}
                seletedUom={isCreatingReturnToVendor ? PoLine.originalUom : PoLine.expectedUom}
                uoms={PoLine.uomList} />
            </TableCell>
            {canDisplayCostPrice && <TableCell>
              <RoundAndFormatValue value={PoLine.unitCostPrice ?? 0}
                currencyCode={currencyCode} />
            </TableCell>}
            {canDisplayCostPrice && <TableCell>
              <RoundAndFormatValue value={calculateLineTotalCostPrice(PoLine)}
                currencyCode={currencyCode} />
            </TableCell>}
            <TableCell>
              {PoLine.lineNumber}
            </TableCell>
            {isShowingTrafficCode && <TableCell>
              {PoLine.tariffCode}
            </TableCell>}
            <TableCell>
              {isEditable &&
                <Tooltip title={PoLine.note}>
                  <IconButton onClick={($event) => { handleNoteButtonClick(PoLine.id, PoLine.note); $event.stopPropagation(); }}>
                    {PoLine.note != '' ? <EditNoteIcon /> : <AddIcon />}
                  </IconButton>
                </Tooltip>}
              {!isEditable && PoLine.note != '' &&
                <Tooltip title={PoLine.note}>
                  <IconButton onClick={($event) => { handleNoteButtonClick(PoLine.id, PoLine.note); $event.stopPropagation(); }}>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>}
            </TableCell>
          </TableRow>)}
      </TableBody>
      {canDisplayTotalCostPrice &&
        <TableFooter>
          <TableRow>
            <TableCell colSpan={visibleColumnCount}
              align='right'>
              <Typography variant='textSM'
                fontWeight='bold'
                component='span'
                sx={{ mr: '8px' }}>
                {t('totalCost')}:
              </Typography>
              <Typography variant='textSM'
                fontWeight='bold'
                component='span'>
                {applyRoundAndFormat(totalCostPrice, currencyCode)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableFooter>}
    </Table>
  </TableContainer>;
};

export default PurchaseOrdersDetailsTable;
